import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import LocationOffIcon from '@material-ui/icons/LocationOff';
import PublicIcon from '@material-ui/icons/Public';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    textAlign: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1)
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  },
  icon: {
    fontSize: 64,
    color: theme.palette.warning.main
  },
  dialogContent: {
    textAlign: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  message: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    fontSize: '1.1rem',
    lineHeight: 1.6
  },
  regionBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  regionLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    textTransform: 'uppercase'
  },
  regionChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500
  },
  dialogActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(1)
  },
  backButton: {
    minWidth: 150,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1.5, 4)
  },
  detailText: {
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    marginTop: theme.spacing(2)
  }
}));

/**
 * GeoLockDialog Component
 * 
 * Displays a blocking dialog when a user tries to access a geo-restricted job
 * from a location that is not allowed by the job poster.
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {string} allowedRegions - Comma-separated string of allowed regions
 * @param {Object} userLocation - User's detected location
 */
const GeoLockDialog = ({ open, onClose, allowedRegions, userLocation }) => {
  const classes = useStyles();

  // Parse allowed regions into array for display
  const parseRegions = (regions) => {
    if (!regions) return [];
    return regions
      .split(/[,;]/)
      .map(r => r.trim())
      .filter(r => r.length > 0);
  };

  const regionsList = parseRegions(allowedRegions);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="geolock-dialog-title"
      aria-describedby="geolock-dialog-description"
    >
      <DialogTitle className={classes.dialogTitle} id="geolock-dialog-title">
        <Box className={classes.iconContainer}>
          <LocationOffIcon className={classes.icon} />
        </Box>
        <Typography variant="h4" component="div">
          Location Restricted Job
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent} id="geolock-dialog-description">
        <Typography className={classes.message}>
          This job posting is restricted to applicants in specific geographic regions. 
          Based on your current location, you are not eligible to apply for this position.
        </Typography>

        {regionsList.length > 0 && (
          <Box className={classes.regionBox}>
            <Typography className={classes.regionLabel}>
              <PublicIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
              Allowed Regions
            </Typography>
            <Box>
              {regionsList.map((region, index) => (
                <Chip
                  key={index}
                  label={region}
                  color="primary"
                  variant="outlined"
                  className={classes.regionChip}
                />
              ))}
            </Box>
          </Box>
        )}

        {userLocation && userLocation.country && (
          <Typography className={classes.detailText}>
            Your detected location: {userLocation.city && `${userLocation.city}, `}
            {userLocation.country}
          </Typography>
        )}

        <Typography className={classes.detailText}>
          If you believe this is an error, please contact the job poster directly.
        </Typography>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          className={classes.backButton}
          autoFocus
        >
          Go Back
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GeoLockDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  allowedRegions: PropTypes.string,
  userLocation: PropTypes.shape({
    country: PropTypes.string,
    countryCode: PropTypes.string,
    region: PropTypes.string,
    city: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    source: PropTypes.string
  })
};

GeoLockDialog.defaultProps = {
  allowedRegions: '',
  userLocation: null
};

export default GeoLockDialog;

